import { useNavigate, useSearchParams } from "react-router-dom";
import {
  parseQueryString,
  parseAmplifyConfig,
  composeUri,
  identify,
  cloneSearchParams,
  useAuth,
  composeToken,
  get,
} from "@warden-ui/common";
import { useEffect, useMemo, useState } from "react";
import { CupixWorksApi, WardenApi } from "@warden-ui/api";
import { CardContentError, CPPageBody } from "@warden-ui/common-components";
import { CardContentSuccess } from "@warden-ui/card-components";
import { CupixWorksDomainPostfix } from "@warden-ui/common";
import { useTranslation } from "react-i18next";

const defaultQueryString: { [key: string]: string } = {
  team_domain: "",
  region: "",
  config: "",
  context: "",
  skip_team: "false",
  error_description: "", // NOTE: error from cognito
  oauth_flow: "false",
  clear: "false",
};

export default function RedirectToRedirectUri() {
  const auth = useAuth();
  const [searchParams] = useSearchParams();  
  const navigate = useNavigate();
  const [t] = useTranslation();

  searchParams.set("clear", "false"); // redirect page should not be cleared to avoid infinite loop
  const queryString = parseQueryString(searchParams, defaultQueryString);
  const amplifyConfig = useMemo(() => parseAmplifyConfig(queryString), [queryString]);

  const [logoUrl, setLogoUrl] = useState("");
  const [teamDomain, setTeamDomain] = useState("");
  const [errorMessage, setErrorMessage] = useState(queryString.error_description);
  const [sessionData, setSessionData] = useState({});

  useEffect(() => {
    auth.configureAmplify(amplifyConfig);
  }, [auth, amplifyConfig]);

  const redirectToSignin = async () => {
    await auth.signOut(amplifyConfig);

    searchParams.set("clear", "false");
    searchParams.delete("error");
    searchParams.delete("error_description");
    navigate(`/?${searchParams.toString()}`);
  };

  const saveToken = async (authSession: any) => {
    const accessToken = authSession.tokens?.accessToken.toString() || '';
    const body = composeToken(authSession);

    const resp = await WardenApi.saveToken(accessToken, body);
    return resp.code;
  };

  const redirectTo = async () => {
    const redirectUri = amplifyConfig[amplifyConfig.region]?.redirectUri;
    if (!redirectUri) {
      console.error("Redirect URI is not configured.");
      return;
    }

    console.log("redirectTo", redirectUri);

    try {
      const authSession = await auth.fetchAuthSession(amplifyConfig);
      const results = await Promise.allSettled([
        identify(sessionData),
        saveToken(authSession),
      ]);
      const accessCode = results[1].status === "fulfilled" ? results[1].value : undefined;

      const redirectTarget = composeUri({
        baseUri: redirectUri,
        region: amplifyConfig.region,
        teamDomain: queryString.team_domain,
        authSession: authSession,
        accessCode: accessCode,
        oauthFlow: queryString.oauth_flow,
      });

      console.info(`sign in success: ${authSession.userSub}`);
      window.location.replace(redirectTarget);
    } catch (error) {
      console.error("Failed to redirect:", error);
    }
  };

  const redirectToUpdateUser = (teamDomain: string, teamName: string, userState: string) => {
    const params = cloneSearchParams(searchParams);
    params.set('team_domain', teamDomain ?? '');
    params.set('team_name', teamName ?? '');
    params.set('active', (userState === "active").toString());

    navigate(`/users/update?${params.toString()}`);
  };

  useEffect(() => {
    let redirected = false;

    const handleAuthFlow = async () => {
      if (errorMessage.length > 0) {
        console.error("Error from cognito: ", errorMessage);
        return;
      }

      const cupixWorksApiEndpoint =
        amplifyConfig[amplifyConfig.region]?.cupixworksApiEndpoint;
      if (!cupixWorksApiEndpoint) {
        console.log(`CupixWorks API endpoint is not configured. config: ${queryString.config}`);
        await redirectTo();
        return;
      }

      if (!queryString.skip_team && queryString.team_domain?.length > 0) {
        try {
          const authSession = await auth.fetchAuthSession(amplifyConfig);
          const accessToken = authSession.tokens?.accessToken.toString();
          if (!accessToken) {
            throw new Error("Access token is not found.");
          }

          const identityProvider = get('identity_provider');
          if (identityProvider) {
            console.log("Identity provider detected, calling provision API", identityProvider);
            try {
              await new CupixWorksApi(cupixWorksApiEndpoint)
                .provisionUser(queryString.team_domain, accessToken);
              console.log("User provision completed successfully");
            } catch (provisionError) {
              console.warn("User provision failed, continuing with session:", provisionError);
            }
          }

          const data = await new CupixWorksApi(cupixWorksApiEndpoint)
            .getSession(queryString.team_domain, accessToken);

          setSessionData(data);
          const userName = data?.user.attributes.firstname ?? "";
          if (userName.length === 0) {
            return redirectToUpdateUser(
              data?.team.attributes.domain,
              data?.team.attributes.name,
              data?.user.attributes.state
            );
          }

          setLogoUrl(data?.team.attributes.logo_urls?.thumb);
          setTeamDomain(
            `${queryString.team_domain}${CupixWorksDomainPostfix(
              cupixWorksApiEndpoint
            )}`
          );

          if (data?.user.attributes.state === "active") {
            if (!redirected) await redirectTo();
          } else {
            throw new Error(`User is not active: ${JSON.stringify(data?.user.attributes)}`);
          }
        } catch (error) {
          console.error((error as Error).message);
          setErrorMessage(t('errors.team_access_denied'));
        }
      } else {
        navigate(`/teams/choose?${searchParams.toString()}`);
      }
    };

    handleAuthFlow().catch((error) => {
      console.error("Auth flow error:", error);
      setErrorMessage(t('errors.team_access_denied'));
    });

    return () => {
      redirected = true;
    };
  }, []);

  if (errorMessage.length > 0) {
    let decodedErrorMessage;
    let errorDetails = "";
    if (errorMessage === t('errors.team_access_denied')) {
      decodedErrorMessage = errorMessage;
    } else {
      decodedErrorMessage = t('errors.saml_server');
      errorDetails = errorMessage;
    }

    return (
      <CPPageBody onBackClicked={redirectToSignin}>
        <CardContentError
          message={decodedErrorMessage}
          details={errorDetails}
        />
      </CPPageBody>
    );
  }

  const redirectUri = amplifyConfig[amplifyConfig.region]?.redirectUri;
  if (redirectUri?.includes("connect")) {
    return (
      <CPPageBody>
        <CardContentSuccess
          teamDomain={teamDomain}
          teamLogoUrl={logoUrl}
          onClick={() => redirectTo()}
        />
      </CPPageBody>
    );
  }

  // TODO: Add a loading spinner
  return null;
}
